import React from 'react';
import { Heart, MessageSquare, Users, Bookmark } from 'lucide-react';
import { useRobloky } from '../../context/RoblokyContext';
import { formatCompact, displayLikes } from '../../utils/format';
import { GameImg } from './GameImg';
import type { GameCard as GameCardType } from '../../types';

export const GameCard: React.FC<{ game: GameCardType }> = ({ game }) => {
  const { openGame, likeGame, saveGame } = useRobloky();

  return (
    <article className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0d111a] hover:border-red-500/40 transition-all hover:shadow-[0_0_25px_rgba(226,35,26,0.15)]">
      <button onClick={() => openGame(game.placeId, game.slug)} className="text-left focus:outline-none">
        <div className="relative aspect-video overflow-hidden bg-black/40">
          {game.thumb ? (
            <GameImg
              src={game.thumb}
              altSrc={game.thumbAlt}
              alt={`${game.title} screenshot`}
              className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-slate-600 text-xs font-mono">
              No image
            </div>
          )}
          <span className="absolute left-2 top-2 rounded-md bg-black/70 px-2 py-0.5 text-[10px] font-mono font-bold text-red-300 border border-red-500/30">
            {game.genre}
          </span>
          {game.galleryCount > 1 && (
            <span className="absolute right-2 top-2 rounded-md bg-black/70 px-1.5 py-0.5 text-[10px] font-mono text-slate-300">
              +{game.galleryCount - 1}
            </span>
          )}
        </div>
      </button>

      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <button onClick={() => openGame(game.placeId, game.slug)} className="text-left focus:outline-none">
          <h3 className="text-sm font-bold text-white leading-snug line-clamp-1 group-hover:text-red-300 transition-colors">
            {game.title}
          </h3>
          <p className="text-[11px] font-mono text-slate-500 truncate">by {game.developer}</p>
        </button>

        <div className="flex items-center gap-3 text-[11px] font-mono text-slate-400">
          {game.ccu > 0 && (
            <span className="flex items-center gap-1" title="Players online now">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {formatCompact(game.ccu)}
            </span>
          )}
          {game.likePct > 0 && (
            <span className="text-slate-500">{game.likePct}% liked</span>
          )}
          <span className="flex items-center gap-1 text-slate-500">
            <Users className="h-3 w-3" />
            {formatCompact(game.visits)}
          </span>
        </div>

        <div className="mt-auto flex items-center justify-between pt-1.5 border-t border-white/5">
          <button
            onClick={() => likeGame(game.placeId)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-bold transition-all ${
              game.liked ? 'text-red-400' : 'text-slate-400 hover:text-red-300'
            }`}
            aria-label={game.liked ? 'Unlike' : 'Like'}
          >
            <Heart className={`h-4 w-4 ${game.liked ? 'fill-red-500 text-red-500' : ''}`} />
            {displayLikes(game.upVotes, game.likes, game.liked)}
          </button>
          <button
            onClick={() => openGame(game.placeId, game.slug)}
            className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs text-slate-400 hover:text-slate-200 transition-all"
            aria-label="Comments"
          >
            <MessageSquare className="h-4 w-4" />
            {formatCompact(game.comments)}
          </button>
          <button
            onClick={() => saveGame(game.placeId)}
            className={`p-1.5 rounded-lg transition-all ${game.saved ? 'text-red-400' : 'text-slate-500 hover:text-slate-300'}`}
            aria-label="Save"
          >
            <Bookmark className={`h-4 w-4 ${game.saved ? 'fill-red-500/40' : ''}`} />
          </button>
        </div>
      </div>
    </article>
  );
};
